import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@clientsonramp.com");
  const [password, setPassword] = useState("Admin123!");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null); // {challenge_token, email}
  const [code, setCode] = useState("");

  if (loading) return <div className="p-10">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // We call the raw endpoint so we can detect requires_2fa without
      // AuthContext prematurely thinking we're logged in.
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      if (data.requires_2fa) {
        setChallenge({ challenge_token: data.challenge_token, email: data.email });
      } else {
        await login(email, password); // sync AuthContext
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/auth/2fa/verify-login`,
        { challenge_token: challenge.challenge_token, code: code.trim() },
        { withCredentials: true },
      );
      window.location.href = "/dashboard"; // full reload so AuthContext hydrates
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="lf-auth-panel flex flex-col justify-between p-10 lg:p-16">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center no-underline">
            <img
              src="/logo-on-dark.png"
              alt="Clients OnRamp"
              style={{ height: 76, width: "auto", display: "block" }}
            />
          </Link>
          <Link to="/" className="text-white/60 hover:text-white text-sm flex items-center gap-1 transition">
            <ArrowLeft className="w-3 h-3" /> Public site
          </Link>
        </div>
        <div className="lf-fade-up">
          <div className="overline text-white/60 mb-4">Ops console · v2</div>
          <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter text-white leading-none mb-6">
            Find local businesses without a website.
            <br />
            <span style={{ color: "#FFD700" }}>Close them by Friday.</span>
          </h1>
        </div>
        <div className="overline text-white/40 flex items-center gap-3">
          <span>© Clients OnRamp</span><span>·</span>
          <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-white/70">Privacy</a>
          <span>·</span>
          <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-white/70">Terms</a>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {challenge ? (
          <form onSubmit={submit2fa} className="w-full max-w-sm" data-testid="login-2fa-form">
            <div className="overline mb-3 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Two-factor authentication</div>
            <h2 className="font-display text-3xl font-bold mb-6 tracking-tight">Enter your 6-digit code</h2>
            <p className="text-sm text-neutral-600 mb-6">
              Open your authenticator app for <strong>{challenge.email}</strong> and enter the current code.
              Backup codes work too.
            </p>
            <input
              data-testid="login-2fa-code"
              autoFocus
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
              className="w-full px-4 py-4 border border-neutral-300 bg-white focus:outline-none focus:border-black mb-6 font-mono text-2xl tracking-widest text-center"
              placeholder="000000"
              maxLength={10}
              required
            />
            {error && <div className="pill pill-closed_lost mb-4">{error}</div>}
            <button type="submit" disabled={busy} data-testid="login-2fa-submit" className="lf-btn-primary w-full">
              {busy ? "Verifying…" : "Verify & sign in →"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="w-full mt-3 text-xs text-neutral-500 hover:text-neutral-900 font-mono uppercase tracking-widest">
              ← Use different account
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm lf-fade-up" autoComplete="on">
            <div className="overline mb-3">Restricted access</div>
            <h2 className="font-display text-3xl font-bold mb-8 tracking-tight">Sign in to your console</h2>
            <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">Email</label>
            <input data-testid={AUTH.emailInput} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black mb-5 font-mono text-sm" />
            <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">Password</label>
            <input data-testid={AUTH.passwordInput} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black mb-6 font-mono text-sm" placeholder="••••••••" />
            {error && <div className="pill pill-closed_lost mb-4">{error}</div>}
            <button data-testid={AUTH.submitBtn} type="submit" disabled={busy} className="lf-btn-primary w-full">
              {busy ? "Signing in…" : "Sign in →"}
            </button>
            <div className="mt-4 text-center">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-neutral-500 hover:text-black transition">
                Forgot your password?
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
